import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Hero() {
  const navigate = useNavigate();
  const [starterPrompt, setStarterPrompt] = useState('');

  const handleStarterSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const idea = starterPrompt.trim();
    navigate(idea ? `/quick-start?idea=${encodeURIComponent(idea)}` : '/quick-start');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=abstract%20futuristic%20digital%20advertising%20technology%20background%20with%20flowing%20glowing%20amber%20orange%20light%20streams%20data%20visualization%20particles%20floating%20in%20deep%20dark%20charcoal%20black%20space%20ultra%20detailed%20cinematic%20render%20professional%20startup%20aesthetic%20wide%20panoramic%20composition&width=1440&height=900&seq=adforge-hero-bg-01&orientation=landscape')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950/85 via-stone-950/75 to-stone-950/90" />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 lg:px-8 text-center pt-20">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
          <span className="text-sm font-medium text-white/90">AI copy <span className="text-orange-300">+</span> AI creative images <span className="text-orange-300">+</span> Visual editor — all in one</span>
        </div>

        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-amber-400/20 border border-orange-400/30 rounded-full px-4 py-1.5 mb-8 backdrop-blur-sm">
          <i className="ri-sparkling-2-fill text-amber-300 text-xs" />
          <span className="text-sm font-semibold text-amber-200">New: Social Posts Generator — create organic content for every platform</span>
          <Link to="/social-quick-start" className="text-xs font-bold text-orange-300 hover:text-orange-200 underline underline-offset-2 transition-colors duration-200 whitespace-nowrap">Try it →</Link>
        </div>

        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight mb-6">
          Generate Better Ads
          <br />
          <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
            &amp; Social Posts with AI
          </span>
        </h1>

        <p className="text-lg md:text-xl text-white/75 max-w-2xl mx-auto mb-10 leading-relaxed">
          The most complete{' '}
          <Link
            to="/ai-ad-generator"
            className="text-orange-300 hover:text-orange-200 underline underline-offset-2 decoration-orange-400/50 transition-colors duration-200"
          >
            AI ad generator
          </Link>{' '}
          for small businesses — AI-written copy, AI-generated images, and now organic social post captions for Facebook, Instagram, LinkedIn, TikTok &amp; more.
        </p>

        <form
          onSubmit={handleStarterSubmit}
          className="mx-auto mb-5 flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-white/15 bg-white/10 p-2 shadow-2xl shadow-black/20 backdrop-blur-md sm:flex-row"
        >
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-xl bg-white px-4 py-3 text-left">
            <i className="ri-magic-line text-lg text-orange-500" />
            <input
              value={starterPrompt}
              onChange={(event) => setStarterPrompt(event.target.value)}
              placeholder="What do you want to promote today?"
              className="min-w-0 flex-1 bg-transparent text-sm font-medium text-stone-900 outline-none placeholder:text-stone-400 md:text-base"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-3 text-sm font-extrabold text-white transition-all duration-200 hover:from-orange-600 hover:to-amber-500 sm:px-7"
          >
            Preview Free
            <i className="ri-arrow-right-line" />
          </button>
        </form>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            to="/quick-start"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-base px-8 py-4 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-flashlight-fill" />
            Generate My Ad - Free
          </Link>
          <Link
            to="/social-quick-start"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-200 to-cyan-100 border border-sky-100/80 text-slate-950 font-bold text-base px-8 py-4 rounded-xl shadow-lg shadow-cyan-950/20 hover:from-sky-100 hover:to-cyan-50 transition-all duration-200 whitespace-nowrap"
          >
            <i className="ri-sparkling-2-line" />
            Try Social Posts
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-6 md:gap-12 max-w-2xl mx-auto">
          {[
            { number: '50,000+', label: 'Ads Generated' },
            { number: '3,200+', label: 'Businesses Using It' },
            { number: '4.9★', label: 'Average Rating' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-extrabold text-white">{stat.number}</div>
              <div className="text-sm text-white/60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white/40 text-2xl" />
      </div>
    </section>
  );
}
